import { createAsyncFlow } from '@adv-frontend/modules-flow';
import { memoize } from '@adv-frontend/modules-memo';
import type { BaseRowData, DataProvider } from '../../types';

/** Опции метода `createStaticDataProvider` */
type CreateStaticDataProviderOptions = {
    /** Список ID всех загружаемых строк */
    loadingIds?: string[];
};

/**
 * Метод для получения статических данных.
 * Источник данных - заранее подготовленный массив.
 *
 * @typeParam RowData Данные строки.
 * @param data Массив с заранее подготовленными данными.
 * @param options Набор опций для провайдера данных.
 * @returns Провайдер статических данных.
 */
export function createStaticDataProvider<RowData extends BaseRowData>(
    data: RowData[],
    options?: CreateStaticDataProviderOptions
): DataProvider<RowData> {
    const rowsIds: string[] = Array.from({ length: data.length });

    const loadingRowsIds = new Set<string>();
    const rowsData = new Map<string, RowData>();
    for (let i = 0; i < data.length; i++) {
        const row = data[i];
        if (!row) {
            continue;
        }

        if (options?.loadingIds?.includes(row.id)) {
            loadingRowsIds.add(row.id);
        }

        rowsIds[i] = row.id;
        rowsData.set(row.id, row);
    }

    const loadingRowIds = new Set(options?.loadingIds || []);
    const loadedRows = loadingRowIds.size === 0 ? data : data.filter(row => !loadingRowIds.has(row.id));

    const pendingFlow = createAsyncFlow<RowData>({
        status: 'pending',
    }).asFlow();
    const notFoundErrorFlow = createAsyncFlow<RowData>({
        status: 'error',
        error: new Error('Row not found'),
    }).asFlow();
    const rowFlow = memoize(
        (id: string) => {
            const isLoading = loadingRowsIds.has(id);
            if (isLoading) {
                return pendingFlow;
            }

            const rowData = rowsData.get(id);
            if (rowData === undefined) {
                return notFoundErrorFlow;
            }

            return createAsyncFlow({
                status: 'success',
                data: rowData,
            });
        },
        { maxPinned: 0 }
    );

    const dataProvider: DataProvider<RowData> = {
        totalCount: createAsyncFlow({
            status: 'success',
            data: data.length,
        }),
        rowData: rowFlow,
        rowsIds: createAsyncFlow({
            status: 'success',
            data: rowsIds,
        }),
        loadedRowsData: createAsyncFlow({
            status: 'success',
            data: loadedRows,
        }),
    };

    return dataProvider;
}
